<template>
  <div class="container">
    <h1>拉卡拉支付</h1>
    
    <!-- 支付方式选择 -->
    <div class="payment-method">
      <h3>选择支付方式:</h3>
      <div class="method-buttons">
        <button 
          :class="{ active: paymentMethod === 'alipay' }" 
          @click="paymentMethod = 'alipay'"
        >
          支付宝支付
        </button>
        <button 
          :class="{ active: paymentMethod === 'wechat' }" 
          @click="paymentMethod = 'wechat'"
        >
          微信支付
        </button>
      </div>
    </div>
    
    <!-- 支付宝支付表单 -->
    <div v-if="paymentMethod === 'alipay'">
      <div class="note">
        <h3>支付宝支付说明</h3>
        <p>请输入以下信息以生成支付宝支付二维码：</p>
        <ul>
          <li><strong>商户号</strong>：拉卡拉分配的商户号</li>
          <li><strong>终端号</strong>：拉卡拉分配的终端号</li>
          <li><strong>外部订单号</strong>：商户系统中的订单号（需唯一）</li>
          <li><strong>交易金额</strong>：单位为分</li>
        </ul>
      </div>
      
      <form @submit.prevent="submitAlipayForm">
        <div class="form-group">
          <label for="merchantNo">商户号:</label>
          <input type="text" id="merchantNo" v-model="alipayForm.merchantNo" required>
        </div>
        
        <div class="form-group">
          <label for="termNo">终端号:</label>
          <input type="text" id="termNo" v-model="alipayForm.termNo" required>
        </div>
        
        <div class="form-group">
          <label for="outTradeNo">外部订单号:</label>
          <input type="text" id="outTradeNo" v-model="alipayForm.outTradeNo" required>
        </div>
        
        <div class="form-group">
          <label for="totalAmount">交易金额(分):</label>
          <input type="number" id="totalAmount" v-model.number="alipayForm.totalAmount" min="1" required>
        </div>
        
        <button type="submit" id="submitBtn" :disabled="loading">
          {{ loading ? '生成中...' : '生成支付二维码' }}
        </button>
      </form>
    </div>
    
    <!-- 微信支付表单 -->
    <div v-else-if="paymentMethod === 'wechat'">
      <div class="note">
        <h3>微信支付说明</h3>
        <p>请输入以下信息以发起微信支付：</p>
        <ul>
          <li><strong>用户ID</strong>：微信用户ID</li>
          <li><strong>商户订单号</strong>：商户系统中的订单号（需唯一）</li>
          <li><strong>交易金额</strong>：单位为分</li>
        </ul>
      </div>
      
      <form @submit.prevent="submitWechatForm">
        <div class="form-group">
          <label for="userId">微信用户ID:</label>
          <input type="text" id="userId" v-model="wechatForm.userId" required>
        </div>
        
        <div class="form-group">
          <label for="wechatOutTradeNo">商户订单号:</label>
          <input type="text" id="wechatOutTradeNo" v-model="wechatForm.outTradeNo" required>
        </div>
        
        <div class="form-group">
          <label for="wechatTotalAmount">交易金额(分):</label>
          <input type="number" id="wechatTotalAmount" v-model.number="wechatForm.totalAmount" min="1" required>
        </div>
        
        <button type="submit" id="submitWechatBtn" :disabled="loading">
          {{ loading ? '处理中...' : '发起微信支付' }}
        </button>
      </form>
    </div>
    
    <div class="loading" v-if="loading">
      <div class="spinner"></div>
      <p>正在处理支付请求...</p>
    </div>
    
    <div class="result" :class="resultClass" v-if="result.message">
      <strong>{{ result.type }}:</strong> {{ result.message }}
      <div class="qr-code" v-if="qrCodeUrl">
        <h3>请使用支付宝扫描以下二维码完成支付</h3>
        <img :src="qrCodeImageUrl" alt="支付二维码" style="max-width: 200px; height: auto;">
        <p><small>二维码链接: <a :href="qrCodeUrl" target="_blank">{{ qrCodeUrl }}</a></small></p>
      </div>
      <div v-if="result.details">
        <p><strong>详细信息:</strong> {{ result.details }}</p>
      </div>
      <div v-if="result.errorCode">
        <p><strong>错误代码:</strong> {{ result.errorCode }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'LakalaPayment',
  data() {
    return {
      paymentMethod: 'alipay', // 默认选择支付宝支付
      alipayForm: {
        merchantNo: '8226510739205HJ',
        termNo: 'L8143685',
        outTradeNo: '',
        totalAmount: 1
      },
      wechatForm: {
        userId: '',
        outTradeNo: '',
        totalAmount: 1
      },
      loading: false,
      result: {
        type: '',
        message: '',
        details: '',
        errorCode: ''
      },
      qrCodeUrl: ''
    };
  },
  computed: {
    resultClass() {
      return this.result.type === 'success' ? 'success' : 'error';
    },
    qrCodeImageUrl() {
      if (this.qrCodeUrl) {
        return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(this.qrCodeUrl)}`;
      }
      return '';
    }
  },
  mounted() {
    // 生成随机订单号
    this.alipayForm.outTradeNo = 'ORDER_' + Date.now() + '_' + Math.floor(Math.random() * 1000);
    this.wechatForm.outTradeNo = 'ORDER_' + Date.now() + '_' + Math.floor(Math.random() * 1000);
  },
  methods: {
    async submitAlipayForm() {
      this.loading = true;
      this.result = { type: '', message: '', details: '', errorCode: '' };
      this.qrCodeUrl = '';
      
      try {
        const requestData = {
          totalAmount: this.alipayForm.totalAmount,
          orderDesc: "拉卡拉支付宝支付测试订单"
        };
        
        const response = await api.post('/lakala/alipay/preorder', requestData);
        const data = response.data;
        
        if (data.success) {
          this.result = {
            type: 'success',
            message: data.message
          };
          
          if (data.qrCodeUrl) {
            this.qrCodeUrl = data.qrCodeUrl;
          }
        } else {
          this.result = {
            type: 'error',
            message: data.message,
            details: data.details,
            errorCode: data.errorCode
          };
        }
      } catch (error) {
        console.error('请求失败:', error);
        this.result = {
          type: 'error',
          message: '无法连接到服务器，请检查网络连接或稍后重试。',
          details: '错误详情: ' + error.message
        };
      } finally {
        this.loading = false;
      }
    },
    
    async submitWechatForm() {
      this.loading = true;
      this.result = { type: '', message: '', details: '', errorCode: '' };
      this.qrCodeUrl = '';
      
      try {
        const response = await api.post('/lakala/wechat/preorder', {
          userId: this.wechatForm.userId,
          outTradeNo: this.wechatForm.outTradeNo,
          totalAmount: this.wechatForm.totalAmount.toString()
        });
        
        const data = response.data;
        
        if (data.success) {
          this.result = {
            type: 'success',
            message: data.message,
            details: data.data ? JSON.stringify(data.data) : ''
          };
        } else {
          this.result = {
            type: 'error',
            message: data.message,
            details: data.details,
            errorCode: data.errorCode
          };
        }
      } catch (error) {
        console.error('请求失败:', error);
        this.result = {
          type: 'error',
          message: '无法连接到服务器，请检查网络连接或稍后重试。',
          details: '错误详情: ' + error.message
        };
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
body {
  font-family: Arial, sans-serif;
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background-color: #f5f5f5;
}
.container {
  background-color: white;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}
h1 {
  color: #333;
  text-align: center;
}
.payment-method {
  margin-bottom: 30px;
}
.payment-method h3 {
  margin-bottom: 15px;
}
.method-buttons {
  display: flex;
  gap: 10px;
}
.method-buttons button {
  flex: 1;
  padding: 12px;
  border: 1px solid #ddd;
  background-color: #f8f9fa;
  cursor: pointer;
  border-radius: 5px;
  transition: all 0.3s;
}
.method-buttons button.active {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}
.form-group {
  margin-bottom: 20px;
}
label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}
input, textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  box-sizing: border-box;
}
button {
  background-color: #007bff;
  color: white;
  padding: 12px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  width: 100%;
}
button:hover {
  background-color: #0056b3;
}
button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}
.result {
  margin-top: 20px;
  padding: 15px;
  border-radius: 5px;
}
.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}
.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}
.loading {
  text-align: center;
}
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #007bff;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin: 0 auto 10px;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.qr-code {
  text-align: center;
  margin-top: 20px;
}
.note {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 5px;
  padding: 15px;
  margin-bottom: 20px;
  color: #856404;
}
</style>